/**
 * HTTP-клиент API.
 *
 * Все запросы идут с `credentials: 'include'`: авторизация построена на
 * HttpOnly-cookie, и без этого флага сессия не передаётся.
 *
 * Базовый путь относительный (`/api`). В dev-режиме его проксирует Vite,
 * в продакшене — обратный прокси. Так фронтенд и API остаются на одном
 * origin, и сессионная cookie работает с SameSite=Lax.
 */

import type {
  AppearanceSettings,
  ApiErrorBody,
  DeviceInfo,
  LatLng,
  PositionSource,
  RouteGeometry,
  RouteState,
  RouteSummary,
  TelemetrySnapshot,
  UnitSystem,
  UserProfile,
  UserSettings,
  Waypoint,
  WaypointOrigin,
} from '@gpstracker/shared';

const BASE = '/api';

/** Ошибка API с машинночитаемым кодом. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: Record<string, string[]>;

  constructor(status: number, code: string, message: string, details?: Record<string, string[]>) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    if (details) this.details = details;
  }

  /** Сессия истекла или отсутствует — нужно показать вход. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  let response: Response;

  try {
    response = await fetch(`${BASE}${path}`, {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers: options.body !== undefined ? { 'content-type': 'application/json' } : {},
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // Отличаем отмену запроса от реальной сетевой ошибки: при размонтировании
    // компонента показывать пользователю ошибку не нужно.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'network_error', 'Нет связи с сервером');
  }

  if (response.status === 204) return undefined as T;

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorBody | null;
    throw new ApiError(
      response.status,
      body?.error.code ?? 'unknown',
      body?.error.message ?? `Ошибка запроса (HTTP ${response.status})`,
      body?.error.details,
    );
  }

  return (await response.json()) as T;
}

// ─────────────────────────────────────────────────────────────
// Типы ответов
// ─────────────────────────────────────────────────────────────

export interface MapConfig {
  tileUrl: string;
  attribution?: string;
}

export interface OverlayLinkView {
  tokenHint: string;
  enabled: boolean;
  createdAt: string;
  lastViewedAt: string | null;
}

/** Ответ на генерацию новой ссылки. */
export interface OverlayLinkWithToken extends OverlayLinkView {
  token: string;
  url: string;
}

export interface DeviceWithToken extends DeviceInfo {
  ingestToken: string;
  ingestUrl: string;
}

export interface BootstrapData {
  user: UserProfile;
  settings: UserSettings;
  overlay: OverlayLinkView | null;
  devices: DeviceInfo[];
  map: MapConfig;
}

export interface RouteView {
  id: string;
  state: RouteState;
  activityType: UserSettings['tracking']['activityType'];
  start: Waypoint;
  destination: Waypoint;
  geometry: RouteGeometry;
  distanceTotalM: number;
  plannedDurationS: number | null;
  routingProvider: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
}

export interface RouteSegments {
  planned: LatLng[];
  covered: LatLng[];
  remaining: LatLng[];
}

export interface ActiveRouteResponse {
  route: RouteView;
  telemetry: TelemetrySnapshot | null;
  segments: RouteSegments | null;
}

export interface GeocodeResult extends LatLng {
  label: string;
  address: string;
  kind: string | null;
}

export interface HistoryItem {
  route: RouteView;
  summary: RouteSummary | null;
}

export interface WaypointInput extends LatLng {
  label: string | null;
  address: string | null;
  origin: WaypointOrigin;
}

export interface CreateRoutePayload {
  start: WaypointInput;
  destination: WaypointInput;
  useStraightLine?: boolean;
  startImmediately?: boolean;
  settings?: Partial<UserSettings['tracking']>;
}

export interface OverlayPayload {
  appearance: AppearanceSettings;
  /** Система единиц владельца: оверлей не знает его настроек иначе. */
  unitSystem: UnitSystem;
  map: MapConfig | null;
  telemetry: TelemetrySnapshot | null;
  segments: RouteSegments | null;
  summary: RouteSummary | null;
  track: LatLng[];
  streamer: { displayName: string } | null;
}

export type RouteAction = 'start' | 'pause' | 'resume' | 'complete' | 'abandon';

// ─────────────────────────────────────────────────────────────
// Методы
// ─────────────────────────────────────────────────────────────

export const api = {
  /** Профиль текущего пользователя. Бросает ApiError(401) для гостя. */
  me: () => request<{ user: UserProfile }>('/auth/me'),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /** Полный URL входа через Twitch: редирект выполняет браузер. */
  loginUrl: (returnTo = '/dashboard') =>
    `${BASE}/auth/twitch/login?returnTo=${encodeURIComponent(returnTo)}`,

  bootstrap: () => request<BootstrapData>('/account/bootstrap'),

  getSettings: () => request<{ settings: UserSettings }>('/account/settings'),

  updateSettings: (patch: {
    tracking?: Partial<UserSettings['tracking']>;
    appearance?: Partial<Omit<AppearanceSettings, 'visibility'>> & {
      visibility?: Partial<AppearanceSettings['visibility']>;
    };
  }) =>
    request<{ settings: UserSettings }>('/account/settings', {
      method: 'PATCH',
      body: patch,
    }),

  listDevices: () => request<{ devices: DeviceInfo[] }>('/account/devices'),

  createDevice: (name: string, source: PositionSource) =>
    request<{ device: DeviceWithToken }>('/account/devices', {
      method: 'POST',
      body: { name, source },
    }),

  revokeDevice: (id: string) =>
    request<void>(`/account/devices/${id}`, { method: 'DELETE' }),

  getDeviceLink: (id: string) => request<{ url: string | null }>(`/account/devices/${id}/link`),

  restoreDeviceLink: (id: string, url: string) =>
    request<{ url: string }>(`/account/devices/${id}/link`, { method: 'POST', body: { url } }),

  getOverlayLink: () => request<{ url: string | null }>('/account/overlay-link'),

  restoreOverlayLink: (url: string) =>
    request<{ url: string }>('/account/overlay-link/restore', { method: 'POST', body: { url } }),

  createOverlayLink: () =>
    request<{ overlay: OverlayLinkWithToken }>('/account/overlay-link', {
      method: 'POST',
    }),

  setOverlayLinkEnabled: (enabled: boolean) =>
    request<{ overlay: OverlayLinkView }>('/account/overlay-link', {
      method: 'PATCH',
      body: { enabled },
    }),

  deleteAccount: (confirmLogin: string) =>
    request<void>('/account/delete', { method: 'POST', body: { confirmLogin } }),

  /** Предпросмотр геометрии без сохранения маршрута. */
  previewRoute: (start: LatLng, destination: LatLng, signal?: AbortSignal) =>
    request<{ geometry: RouteGeometry }>('/routes/preview', {
      method: 'POST',
      body: { start, destination },
      ...(signal ? { signal } : {}),
    }),

  /** Активный маршрут. null — активного маршрута нет (HTTP 204). */
  activeRoute: () =>
    request<ActiveRouteResponse | undefined>('/routes/active').then(
      (value) => value ?? null,
    ),

  createRoute: (payload: CreateRoutePayload) =>
    request<{ route: RouteView; telemetry: TelemetrySnapshot | null }>('/routes', {
      method: 'POST',
      body: payload,
    }),

  updateRoute: (routeId: string, payload: Pick<CreateRoutePayload, 'start' | 'destination' | 'useStraightLine'>) =>
    request<ActiveRouteResponse>(`/routes/${routeId}`, { method: 'PATCH', body: payload }),

  telemetry: (routeId: string) =>
    request<{ telemetry: TelemetrySnapshot }>(`/routes/${routeId}/telemetry`),

  segments: (routeId: string) =>
    request<{ segments: RouteSegments }>(`/routes/${routeId}/segments`),

  routeAction: (routeId: string, action: RouteAction) =>
    request<{ telemetry?: TelemetrySnapshot; summary?: RouteSummary | null }>(
      `/routes/${routeId}/actions`,
      { method: 'POST', body: { action } },
    ),

  routeSummary: (routeId: string) =>
    request<{ summary: RouteSummary; track: LatLng[] }>(`/routes/${routeId}/summary`),

  history: (limit = 20, cursor?: string) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor) params.set('cursor', cursor);
    return request<{ items: HistoryItem[]; nextCursor: string | null }>(
      `/routes/history?${params.toString()}`,
    );
  },

  deleteRoute: (routeId: string) =>
    request<void>(`/routes/${routeId}`, { method: 'DELETE' }),

  searchPlaces: (query: string, near?: LatLng, signal?: AbortSignal) => {
    const params = new URLSearchParams({ q: query });
    if (near) {
      params.set('lat', String(near.lat));
      params.set('lon', String(near.lon));
    }
    return request<{ results: GeocodeResult[] }>(`/geo/search?${params.toString()}`, {
      ...(signal ? { signal } : {}),
    });
  },

  reverseGeocode: (point: LatLng, signal?: AbortSignal) =>
    request<{ place: GeocodeResult | null }>(
      `/geo/reverse?lat=${point.lat}&lon=${point.lon}`,
      { ...(signal ? { signal } : {}) },
    ),

  /** Данные публичного оверлея. Авторизация — токеном в пути. */
  overlay: (token: string, signal?: AbortSignal) =>
    request<OverlayPayload>(`/overlay/${token}`, { ...(signal ? { signal } : {}) }),
};
